import React, { useMemo, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { formatBooleanTrueFalse, lineColors } from "../../../../utils";
import { PermitActionsCell } from "../components";
import styled from "styled-components/macro";
import { Chip as MuiChip } from "@material-ui/core";
import { spacing } from "@material-ui/system";

// Styled chip for associated wells
const Chip = styled(MuiChip)`
  ${spacing};
  height: 20px;
  margin-top: 1px;
  margin-bottom: 1px;
  padding: 4px 0;
  font-size: 90%;
  background-color: ${(props) => props.rgbcolor};
  color: ${(props) => props.theme.palette.common.white};
`;

/**
 * Column cell style configurations
 * @constant {Object}
 */
const CELL_STYLES = {
  actions: { width: 120, minWidth: 120, padding: "0 4px" },
  aggSystemName: { width: 145, minWidth: 145 },
  permitHolder: { width: 180, minWidth: 180 },
  permitTerms: { width: 300, minWidth: 300 },
  notes: { width: 300, minWidth: 300 },
  specialProvisions: { width: 300, minWidth: 300 },
};

/**
 * Custom hook for generating permit table columns
 * Includes action column with download/edit buttons
 *
 * @param {Function} onDownload - Callback for download action (type, rowData)
 * @param {*} resetKey - Key that forces columns to recreate (prevents MaterialTable mutation accumulation)
 * @returns {Array} Array of MaterialTable column definitions
 */
const usePermitColumns = (onDownload, resetKey) => {
  const history = useHistory();

  /**
   * Handles navigation to permit edit page
   * @param {string|number} id - Permit ID
   */
  const handleEdit = useCallback(
    (id) => {
      if (id) {
        history.push("/models/dm-permits/" + id);
      }
    },
    [history]
  );

  const columns = useMemo(
    () => [
      {
        title: "Actions",
        field: "actions",
        sorting: false,
        filtering: false,
        export: false,
        cellStyle: CELL_STYLES.actions,
        render: (rowData) => (
          <PermitActionsCell
            rowData={rowData}
            onDownload={onDownload}
            onEdit={handleEdit}
          />
        ),
      },
      {
        title: "Agg System Name",
        field: "agg_system_name",
        cellStyle: CELL_STYLES.aggSystemName,
      },
      {
        title: "Permit Holder",
        field: "permit_holder",
        cellStyle: CELL_STYLES.permitHolder,
      },
      { title: "Permit Number", field: "permit_number" },
      { title: "Permit Year", field: "permit_year" },
      { title: "Permitted Value", field: "permitted_value" },
      { title: "Permitted Use", field: "permitted_use" },
      { title: "Expiration Date", field: "expiration_date" },
      {
        title: "Assoc Wells",
        field: "assoc_wells",
        render: (rowData) =>
          rowData.assoc_wells?.map((item) => (
            <Chip label={item} rgbcolor={lineColors.blue} key={item} mr={1} />
          )),
      },
      {
        title: "Permit Terms",
        field: "permit_terms",
        cellStyle: CELL_STYLES.permitTerms,
      },
      {
        title: "Exportable?",
        field: "exportable",
        render: (rowData) => formatBooleanTrueFalse(rowData.exportable),
      },
      { title: "Exportable Amount", field: "exportable_amount" },
      {
        title: "Notes",
        field: "permit_data_notes",
        cellStyle: CELL_STYLES.notes,
      },
      {
        title: "Special Provisions",
        field: "special_provisions",
        cellStyle: CELL_STYLES.specialProvisions,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onDownload, handleEdit, resetKey]
  );

  return columns;
};

export default usePermitColumns;
